import React, { useMemo, useState } from "react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { Sparkles } from "lucide-react";
import Spinner from "components/Graphs/InlineSpinner";
import type User from "../../entities/User.entity";
import agent from "server";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";

import type { UserContextType } from "contexts/UserContext";
import { toast } from "utils/toast";
import DevToggle from "components/DevToggle";
import { createDefaultAnalyticsObject } from "components/Analytics";
import { cx } from "components/Graphs/utils";
import Modal from "components/Modals/Modal";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";

interface ProfileDropDownProps {
  me: User | null;
  history: any;
  darkMode: boolean;
  toggleDarkMode: () => void;
  userContext: UserContextType;
  className?: string;
  triggerClassName?: string;
  contentAlign?: "start" | "center" | "end";
  contentSide?: "top" | "right" | "bottom" | "left";
  variant?: "avatar" | "account-row";
  modal?: boolean;
  reloadPage?: () => void;
}

export const ProfileDropDown: React.FC<ProfileDropDownProps> = (props) => {
  const applicationShellPreference = useApplicationShellPreference();
  const [pendingShellPreference, setPendingShellPreference] = useState<
    "legacy" | "redesigned" | null
  >(null);
  const [shellPreferenceDialogTarget, setShellPreferenceDialogTarget] =
    useState<"legacy" | "redesigned" | null>(null);
  const isSwitchingToRedesigned = shellPreferenceDialogTarget === "redesigned";

  const openShellPreferenceDialog = (
    shellPreference: "legacy" | "redesigned",
  ) => {
    setShellPreferenceDialogTarget(shellPreference);
    setPendingShellPreference(shellPreference);
  };

  const closeShellPreferenceDialog = () => {
    setPendingShellPreference(null);
  };

  const userNavigationGroups = useMemo(
    () => [
      [
        { name: "Account", href: "/settings/account" },
        { name: "Install CLI", href: "/settings/cli" },
        { name: "Support / Help Center", href: "/help" },
      ],
      [
        {
          name: "Terms of Use",
          action: () =>
            window.open(
              "https://developer.nvidia.com/legal/terms",
              "_blank",
              "noopener,noreferrer",
            ),
        },
        {
          name: "Privacy Policy",
          action: () =>
            window.open(
              "https://www.nvidia.com/en-us/about-nvidia/privacy-policy/",
              "_blank",
              "noopener,noreferrer",
            ),
        },
      ],
      [{ name: "Sign out", href: "/logout" }],
    ],
    [],
  );

  const confirmShellPreference = () => {
    if (!applicationShellPreference || !pendingShellPreference) return;

    applicationShellPreference.chooseMode(pendingShellPreference);

    (props.reloadPage ?? (() => window.location.reload()))();
  };

  const avatar = props.me?.profilePhotoUrl ? (
    <img
      className="h-8 w-8 rounded-full"
      src={props.me.profilePhotoUrl}
      alt=""
    />
  ) : (
    <div className="h-8 w-8 rounded-full border border-zinc-800 hover:brightness-150 flex items-center justify-center text-white">
      {props.userContext.isLoading ? (
        <Spinner className="h-4 w-4" />
      ) : (
        props.me?.name?.[0]?.toUpperCase() || "?"
      )}
    </div>
  );

  const trigger =
    props.variant === "account-row" ? (
      <button
        className={cx(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-zinc-300 transition hover:bg-[#181a1b] hover:text-white",
          props.triggerClassName,
        )}
        type="button"
        aria-label="Open user menu"
      >
        {avatar}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">
            {props.me?.name || "Account"}
          </span>
          <span className="block truncate text-xs text-zinc-400">
            {props.me?.email || "Profile and settings"}
          </span>
        </span>
        <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-zinc-500" />
      </button>
    ) : (
      <button
        className={cx(
          "max-w-xs flex items-center text-sm rounded-full cursor-pointer p-1 pr-0",
          props.triggerClassName,
        )}
        type="button"
        aria-label="Open user menu"
      >
        {avatar}
      </button>
    );

  return (
    <div className={cx("ml-3 relative", props.className)}>
      <DropdownMenu modal={props.modal}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

        <DropdownMenuContent
          className="min-w-48 max-w-96"
          align={props.contentAlign || "end"}
          side={props.contentSide || "bottom"}
        >
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <div className="truncate text-sm">{props.me?.name}</div>
              <div className="text-gray-400 text-xs truncate">
                {props.me?.email}
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuGroup>
            {userNavigationGroups.map((group, groupIndex) =>
              group.length ? (
                <React.Fragment key={group[0].name}>
                  <DropdownMenuSeparator />
                  {group.map((item) => (
                    <DropdownMenuItem
                      key={item.name}
                      onClick={() => {
                        if (item.action) {
                          item.action();
                        } else {
                          props.history.push(item.href);
                        }
                      }}
                    >
                      {item.name}
                    </DropdownMenuItem>
                  ))}
                  {groupIndex === 0 &&
                    applicationShellPreference?.canChoose && (
                      <DropdownMenuCheckboxItem
                        checked={isApplicationShellRedesigned(
                          applicationShellPreference,
                        )}
                        className="font-semibold"
                        hint="Beta"
                        indicator="switch"
                        onCheckedChange={(checked) => {
                          openShellPreferenceDialog(
                            checked === true ? "redesigned" : "legacy",
                          );
                        }}
                      >
                        <span className="flex items-center gap-1.5">
                          New experience
                          <Sparkles
                            className="size-3.5"
                            fill="currentColor"
                            strokeWidth={0}
                            aria-hidden="true"
                          />
                        </span>
                      </DropdownMenuCheckboxItem>
                    )}
                </React.Fragment>
              ) : null,
            )}
          </DropdownMenuGroup>

          <DevToggle>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                props.userContext.setOnboardingObjectFunc({
                  inviteAccepted: ``,
                });
                const res = await agent.Users.update(
                  props.userContext.me?.id || "",
                  createDefaultAnalyticsObject(props.userContext.me, {
                    inviteAccepted: ``,
                  }),
                );
                if (res.success) {
                  props.userContext.refreshMe();
                  toast.success("Reset onboarding status", {
                    description: "Please refresh the page.",
                  });
                }
              }}
            >
              Click to reset onboarding
            </DropdownMenuItem>
          </DevToggle>
        </DropdownMenuContent>
      </DropdownMenu>
      <Modal
        isOpen={pendingShellPreference !== null}
        setOpen={(isOpen) => {
          if (!isOpen) closeShellPreferenceDialog();
        }}
        onSuccess={confirmShellPreference}
        onClose={closeShellPreferenceDialog}
        title={
          isSwitchingToRedesigned
            ? "Switch to the new experience?"
            : "Switch to the classic experience?"
        }
        body={
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Brev will reload this page to apply the change. Save any work before
            continuing.
          </p>
        }
        confirmLabel={
          isSwitchingToRedesigned
            ? "Use new experience"
            : "Use classic experience"
        }
        cancelLabel="Cancel"
        hideIcon
        severity="info"
      />
    </div>
  );
};
